import React, { useState } from 'react'
import axios from 'axios';
import { HiChevronDown, HiChevronUp, HiOutlineArrowPath } from 'react-icons/hi2';
import { convertTime, formatTimeToWords, timeToSeconds } from '../../../../utils/timeUtils';
import { TimeCell } from './TimeCell';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';

const statusConfig = {
    completed: {
        bg: "#ECFDF5",
        border: "#BBF7D0",
        color: "#15803D",
        dot: "#16A34A",
    },
    void: {
        bg: "#FEF2F2",
        border: "#FECACA",
        color: "#DC2626",
        dot: "#DC2626",
    },
};

const StatusBadge = ({ state }) => {
    const cfg = statusConfig[state.toLowerCase()] ?? statusConfig.Completed;
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border"
            style={{ background: cfg.bg, borderColor: cfg.border, color: cfg.color }}
        >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: cfg.dot }} />
            {state}
        </span>
    );
};

const KitchenOrderListView = ({
    loading,
    error,
    orders,
    token,
    selectedOutlet,
    handleReopenOrder,
    isKitchen,
}) => {
    const [expandedRows, setExpandedRows] = useState({});
    const [rowDetails, setRowDetails] = useState({});
    const [rowLoading, setRowLoading] = useState({});
    const [rowError, setRowError] = useState({});

    const url = process.env.REACT_APP_BASE_URL;

    if (loading || error) return null;

    const toggleRow = async (index, orderId) => {
        const isOpen = !!expandedRows[index];

        // collapse
        if (isOpen) {
            setExpandedRows((prev) => ({ ...prev, [index]: false }));
            return;
        }

        // expand — use cached data if available
        setExpandedRows((prev) => ({ ...prev, [index]: true }));
        if (rowDetails[index]) return;

        try {
            setRowLoading((prev) => ({ ...prev, [index]: true }));
            setRowError((prev) => ({ ...prev, [index]: "" }));

            const response = await axios.post(`${url}/get-order-by-id`, {
                token,
                order_id: String(orderId),
                outlet_name: selectedOutlet,
            });

            setRowDetails((prev) => ({ ...prev, [index]: response.data.data }));
        } catch (err) {
            setRowError((prev) => ({
                ...prev,
                [index]:
                    err?.response?.data?.message ||
                    err?.response?.data?.error ||
                    "Failed to load order details.",
            }));
        } finally {
            setRowLoading((prev) => ({ ...prev, [index]: false }));
        }
    };

    return (
        <div className="bg-white rounded-lg border border-[#E0DDD8] overflow-hidden">
            <div className="overflow-auto tiny-scrollbar max-h-[calc(100vh-225px)] xl:max-h-[calc(100vh-170px)]">
                <table className="w-full text-sm text-center">
                    <thead>
                        <tr className="bg-[#F8F7F3] border-b border-[#E0DDD8] text-xs top-0 sticky z-20">
                            {[
                                "",
                                "KOT ID",
                                "Table",
                                "Ordered At",
                                "Completed At",
                                "Total Time",
                                "Guests",
                                "Type",
                                "State",
                                "Employee",
                                ...(isKitchen ? ["Actions"] : []),
                            ].map((header) => (
                                <th
                                    key={header}
                                    className="px-2 py-3 font-semibold text-[#8a8780] uppercase tracking-widest whitespace-nowrap"
                                >
                                    {header}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {orders.map((order, index) => {
                            const isValidTime = /^\d{2}:\d{2}:\d{2}$/.test(order.TotalTime);

                            return (
                                <React.Fragment key={index}>
                                    {/* ── Main row ── */}
                                    <tr
                                        className="border-b border-[#F8F7F3] hover:bg-[#FAFAF8] transition-colors font-medium cursor-pointer"
                                        onClick={() => toggleRow(index, order.idtblorderTracker)}
                                    >
                                        {/* Toggle chevron */}
                                        <td className="px-1 py-2.5">
                                            <button className="p-1.5 rounded-md hover:bg-[#F3F1EC] transition-colors">
                                                {expandedRows[index]
                                                    ? <HiChevronUp size={18} />
                                                    : <HiChevronDown size={18} />
                                                }
                                            </button>
                                        </td>

                                        <td className="px-2 py-2.5 text-[#2C2C2A]">{order.KOTID}</td>
                                        <td className="px-2 py-2.5 text-[#5a5854]">{order.tableNum}</td>
                                        <td className="px-2 py-2.5 text-[#5a5854]">{convertTime(order.orderedAt)}</td>
                                        <td className="px-2 py-2.5 text-[#5a5854]"><TimeCell value={order.completedAt} convert /></td>
                                        <td className="px-2 py-2.5 text-[#5a5854]"><TimeCell value={order.TotalTime} /></td>
                                        <td className="px-2 py-2.5 text-[#5a5854]">{order.Guest_count}</td>
                                        <td className="px-2 py-2.5 text-[#5a5854] whitespace-nowrap">{order.orderType}</td>

                                        <td className="px-2 py-2.5">
                                            <StatusBadge state={order.currentState} />
                                        </td>

                                        <td className="px-2 py-2.5 text-[#5a5854] whitespace-nowrap">{order.Employee}</td>

                                        {isKitchen && (
                                            <td className="px-2 py-2.5 text-[#5a5854] whitespace-nowrap">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleReopenOrder(order.idtblorderTracker);
                                                    }}
                                                    disabled={!isValidTime}
                                                    title={!isValidTime ? "Cannot reopen this order" : "Reopen Order"}
                                                    className={`
                                                    p-2 rounded-md
                                                    transition-all duration-150
                                                    ${isValidTime
                                                            ? "text-sky-600 hover:bg-sky-100 hover:text-sky-700 active:scale-95"
                                                            : "text-gray-400 cursor-not-allowed opacity-50"
                                                        }
                                                `}
                                                >
                                                    <HiOutlineArrowPath size={18} className="stroke-2" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>

                                    {/* ── Expanded detail row ── */}
                                    {expandedRows[index] && (
                                        <tr>
                                            <td
                                                colSpan={isKitchen ? "11" : "10"}
                                                className="bg-[#FAFAF8] border-b border-[#F0EDE8] px-4 pb-4"
                                            >
                                                {/* Loading */}
                                                {rowLoading[index] && (
                                                    <div className="flex items-center justify-center gap-2 py-6 text-xs text-[#807e76]">
                                                        <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin" />
                                                        <span>Loading items…</span>
                                                    </div>
                                                )}

                                                {/* Error */}
                                                {!rowLoading[index] && rowError[index] && (
                                                    <div className="py-4 text-xs text-red-500 text-center">
                                                        {rowError[index]}
                                                    </div>
                                                )}

                                                {/* Detail table */}
                                                {!rowLoading[index] && !rowError[index] && rowDetails[index] && (
                                                    <table className="w-full text-sm mt-2 text-center">
                                                        <thead>
                                                            <tr className="border-b border-[#E0DDD8]">
                                                                {["Item", "Ordered At", "Completed At", "Total Time", "Qty", "Avg Prep Time", "Prep Difference",].map((h) => (
                                                                    <th
                                                                        key={h}
                                                                        className="p-2.5 font-semibold text-[#8a8780] uppercase text-xs tracking-wider whitespace-nowrap"
                                                                    >
                                                                        {h}
                                                                    </th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {rowDetails[index].OrderItemDetailsList?.map((item, itemIndex) => (
                                                                <tr key={itemIndex} className="border-b border-[#F0EDE8] font-medium">
                                                                    <td className="p-2 text-[#2C2C2A] whitespace-nowrap">{item.ItemName}</td>
                                                                    <td className="p-2 text-[#5a5854]">{convertTime(item.orderedAt)}</td>
                                                                    <td className="p-2 text-[#5a5854]"><TimeCell value={item.completedAt} convert /></td>
                                                                    <td className="p-2 text-[#5a5854]"><TimeCell value={item.TotalTime} /></td>
                                                                    <td className="p-2 text-[#5a5854]">{item.Quantity}</td>
                                                                    <td className="px-2.5 py-2 whitespace-nowrap font-medium text-[#5A5854]">
                                                                        {item.prepTimeDifference ? (
                                                                            formatTimeToWords(item.AvgPrepTime)
                                                                        ) : (
                                                                            <span className="text-[#C7C3BD]">—</span>
                                                                        )}
                                                                    </td>

                                                                    {/* Prep Difference */}
                                                                    <td className="px-2.5 py-2 whitespace-nowrap">
                                                                        {item.prepTimeDifference ? (
                                                                            <span
                                                                                className={`
                                                                                px-2 py-1
                                                                                rounded-full
                                                                                text-xs font-semibold
                                                                                border
                                                                                ${timeToSeconds(item.TotalTime) > timeToSeconds(item.AvgPrepTime)
                                                                                        ? "bg-red-50 text-red-600 border-red-200"
                                                                                        : "bg-green-50 text-green-600 border-green-200"
                                                                                    }
                                                                            `}
                                                                            >
                                                                                {formatTimeToWords(item.prepTimeDifference)}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-[#C7C3BD]">—</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default KitchenOrderListView;